import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { cn } from '../../lib/cn';
import { TOAST_DURATION_MS, dismissToast, showErrorToast, showToast, useToastState } from '../../lib/toast';
import type { Toast } from '../../lib/toast';

/** «Отменено: …» is shown briefly (design: 3 s). */
const UNDONE_TOAST_MS = 3_000;

/** Response of POST /api/v1/undo/:token. */
export interface UndoResponse {
  readonly ok: boolean;
  readonly summary?: string;
}

/** Calls the undo endpoint; on success refreshes every query and confirms with a toast. */
export function useUndo(): (token: string, label?: string) => Promise<boolean> {
  const queryClient = useQueryClient();
  return async (token, label) => {
    try {
      const result = await api.post<UndoResponse>(`/undo/${encodeURIComponent(token)}`);
      await queryClient.invalidateQueries();
      showToast(label ? `Отменено: ${label}` : (result.summary ?? 'Отменено'), { durationMs: UNDONE_TOAST_MS });
      return true;
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Не удалось отменить');
      return false;
    }
  };
}

/**
 * Bottom-centre toast (design Main.dc.html:146-152): navy, paper text, radius 12, shadow-lg, one at
 * a time. «Отменить» calls POST /api/v1/undo/:token; extra actions follow it.
 */
export function Toaster() {
  const { current, liftCount } = useToastState();
  const undo = useUndo();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!current) return undefined;
    const timer = window.setTimeout(() => dismissToast(current.id), current.durationMs ?? TOAST_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [current]);

  const onUndo = async (toast: Toast) => {
    if (!toast.undoToken || busy) return;
    setBusy(true);
    dismissToast(toast.id);
    try {
      await undo(toast.undoToken, toast.text);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed left-1/2 z-[70] -translate-x-1/2"
      style={{ bottom: liftCount > 0 ? 84 : 24 }}
    >
      {current && <ToastView toast={current} busy={busy} onUndo={(toast) => void onUndo(toast)} />}
    </div>
  );
}

interface ToastViewProps {
  readonly toast: Toast;
  readonly busy: boolean;
  readonly onUndo: (toast: Toast) => void;
}

const ACTION_CLASS =
  'h-7 flex-none whitespace-nowrap rounded-ctl border-0 bg-on-navy-action px-2.5 text-[12px] font-semibold text-pi-paper hover:bg-on-navy-action-hover disabled:opacity-40';

function ToastView({ toast, busy, onUndo }: ToastViewProps) {
  const actions = [...(toast.action ? [toast.action] : []), ...(toast.actions ?? [])];
  return (
    <div
      data-tone={toast.tone ?? 'default'}
      className="pointer-events-auto flex max-w-[640px] items-center gap-3 rounded-bar bg-pi-navy py-2.5 pl-4 pr-2.5 text-pi-paper shadow-lg"
    >
      <span className={cn('leading-[1.4]', toast.tone === 'error' && 'text-on-navy-danger')}>{toast.text}</span>
      {toast.undoToken && (
        <button type="button" disabled={busy} onClick={() => onUndo(toast)} className={ACTION_CLASS}>
          Отменить
        </button>
      )}
      {actions.map((action) => (
        <button
          key={action.label}
          type="button"
          onClick={() => {
            dismissToast(toast.id);
            action.onClick();
          }}
          className={ACTION_CLASS}
        >
          {action.label}
        </button>
      ))}
      <button
        type="button"
        onClick={() => dismissToast(toast.id)}
        aria-label="Закрыть"
        className="size-7 flex-none rounded-ctl border-0 bg-transparent text-[16px] text-pi-paper"
      >
        ×
      </button>
    </div>
  );
}
